import { useState, useEffect } from "react";
import api from "@/lib/api";
import CrudTable from "@/components/CrudTable";
import { inr } from "@/lib/format";
import { Lightbulb } from "lucide-react";

export default function Budgeting() {
  const [cats, setCats] = useState(["Revenue", "Salaries", "Rent", "Marketing", "Procurement", "Utilities", "Equipment", "Loan EMI", "Tax", "Other"]);
  const [suggestion, setSuggestion] = useState("");

  useEffect(() => {
    api.get("/settings").then((r) => { if (r.data?.expense_categories?.length) setCats(r.data.expense_categories); }).catch(() => {});
    api.get("/dept-expense").then((r) => {
      const rows = r.data || [];
      const months = [...new Set(rows.map((x) => x.month))].sort();
      const latest = months[months.length - 1];
      const ranked = rows.filter((x) => x.month === latest).sort((a, b) => b.profitability_pct - a.profitability_pct);
      if (ranked.length) {
        setSuggestion(`${ranked[0].department} generated the highest ROI (${ranked[0].profitability_pct}%) in ${latest} — consider increasing its budget allocation next period. ${ranked[ranked.length - 1].department} is the weakest performer and may warrant review.`);
      }
    }).catch(() => {});
  }, []);

  const columns = [
    { key: "month", label: "Month" },
    { key: "category", label: "Category" },
    { key: "budgeted", label: "Budgeted", type: "currency" },
    { key: "actual", label: "Actual", type: "currency" },
    { key: "variance", label: "Variance", type: "signed" },
    { key: "variance_pct", label: "Var %", type: "percent" },
    { key: "status", label: "Status", type: "badge" },
    { key: "remarks", label: "Remarks" },
  ];
  const fields = [
    { key: "month", label: "Month (YYYY-MM)", type: "text", required: true },
    { key: "category", label: "Category", type: "select", options: cats, required: true },
    { key: "budgeted", label: "Budgeted (₹)", type: "number", required: true },
    { key: "actual", label: "Actual (₹)", type: "number", required: true },
    { key: "remarks", label: "Remarks", type: "textarea", full: true },
  ];

  const renderTop = () => suggestion ? (
    <div className="rounded-xl border border-[#C9A24B]/40 bg-[#C9A24B]/8 p-5 flex gap-3" data-testid="allocation-suggestion">
      <Lightbulb className="h-5 w-5 text-[#C9A24B] shrink-0 mt-0.5" />
      <div>
        <div className="font-heading font-medium text-sm mb-1">Allocation Recommendation <span className="text-xs text-slate-400 font-normal">(human-approved, never auto-executed)</span></div>
        <p className="text-sm text-slate-300 leading-relaxed">{suggestion}</p>
      </div>
    </div>
  ) : null;

  return (
    <CrudTable
      testid="budget" endpoint="budgets"
      title="Budgeting" subtitle="Variance, status and warnings are auto-computed. Categories at ≥85% of budget are pushed to the notification bell before overspend happens."
      addLabel="Add Budget Line" columns={columns} fields={fields} renderTop={renderTop}
    />
  );
}
